import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminBrokerAnalytics } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { CalendarRange, RefreshCcw, Wallet, MousePointerClick, UserPlus, Banknote, Layers } from 'lucide-react';

const QUICK_RANGES = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d', label: '7 days' },
  { key: '15d', label: '15 days' },
  { key: '30d', label: '30 days' },
  { key: 'all', label: 'All time' },
];

const BROKER_TABS = [
  { key: 'all', label: 'All Brokers', cls: 'cta-sheen text-white border-0' },
  { key: 'po', label: 'Pocket Option', cls: 'bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.4)] hover:bg-[rgba(245,185,66,0.22)]' },
  { key: 'quotex', label: 'Quotex', cls: 'bg-[rgba(108,176,255,0.14)] text-[hsl(212_80%_70%)] border border-[rgba(108,176,255,0.4)] hover:bg-[rgba(108,176,255,0.22)]' },
];

const FLAGS = {
  BD: '\u{1F1E7}\u{1F1E9}', IN: '\u{1F1EE}\u{1F1F3}', PK: '\u{1F1F5}\u{1F1F0}',
  US: '\u{1F1FA}\u{1F1F8}', GB: '\u{1F1EC}\u{1F1E7}', DE: '\u{1F1E9}\u{1F1EA}',
  AE: '\u{1F1E6}\u{1F1EA}', SA: '\u{1F1F8}\u{1F1E6}', BR: '\u{1F1E7}\u{1F1F7}',
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

// Treat any string that is still a raw {MACRO} placeholder (PO didn't replace) as empty.
function isPlaceholder(v) {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  if (!s) return true;
  return s.length >= 2 && s.startsWith('{') && s.endsWith('}');
}
function clean(v, fallback = '—') {
  return isPlaceholder(v) ? fallback : v;
}

export default function AdminBrokerAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quick, setQuick] = useState('30d');
  const [useCustom, setUseCustom] = useState(false);
  const [start, setStart] = useState(daysAgoIso(30));
  const [end, setEnd] = useState(todayIso());
  const [broker, setBroker] = useState('all');

  const params = useMemo(() => {
    const base = useCustom ? { start, end } : { quick };
    return broker === 'all' ? base : { ...base, broker };
  }, [useCustom, start, end, quick, broker]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminBrokerAnalytics(params));
    } catch {
      // noop
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const s = data?.summary || {};
  const kpis = [
    { Icon: MousePointerClick, label: 'Broker Clicks', value: s.clicks ?? 0, test: 'kpi-broker-clicks' },
    { Icon: UserPlus, label: 'Registrations', value: s.registrations ?? 0, test: 'kpi-broker-regs' },
    { Icon: Banknote, label: 'First Deposits (FTD)', value: s.ftds ?? 0, test: 'kpi-broker-ftds' },
    { Icon: Wallet, label: 'Total Deposits (USD)', value: `$${(s.deposit_sum_usd ?? 0).toLocaleString()}`, test: 'kpi-broker-deposits' },
  ];

  const rates = [
    { label: 'Click → Reg', value: `${s.click_to_reg_rate ?? 0}%` },
    { label: 'Reg → FTD', value: `${s.reg_to_ftd_rate ?? 0}%` },
    { label: 'Email Confirm', value: s.email_confirmations ?? 0 },
    { label: 'Redeposits', value: s.redeposits ?? 0 },
  ];

  const platformChart = (data?.by_platform || []).filter((r) => (r.clicks + r.registrations + r.ftds) > 0);

  return (
    <div className="space-y-5">
      {/* Broker selector */}
      <Card className="card-surface" data-testid="broker-selector">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <Layers className="h-4 w-4 text-[hsl(43_92%_56%)] mr-1" />
          <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mr-2">Broker</span>
          {BROKER_TABS.map((b) => {
            const active = broker === b.key;
            return (
              <Button
                key={b.key}
                size="sm"
                variant="outline"
                onClick={() => setBroker(b.key)}
                className={'h-8 ' + (active ? b.cls : 'border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white')}
                data-testid={`broker-tab-${b.key}`}
              >
                {b.label}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {/* By-broker side-by-side (always shows both regardless of filter) */}
      {(data?.by_broker || []).length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="broker-comparison">
          {(data.by_broker || []).map((b) => {
            const isPO = b.broker === 'po';
            const isQX = b.broker === 'quotex';
            const accent = isPO
              ? 'border-[rgba(245,185,66,0.3)] bg-[rgba(245,185,66,0.04)]'
              : 'border-[rgba(108,176,255,0.3)] bg-[rgba(108,176,255,0.04)]';
            const badgeBg = isPO
              ? 'bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] border border-[rgba(245,185,66,0.42)]'
              : 'bg-[rgba(108,176,255,0.14)] text-[hsl(212_80%_70%)] border border-[rgba(108,176,255,0.42)]';
            const text = isPO ? 'text-[hsl(43_92%_56%)]' : 'text-[hsl(212_80%_70%)]';
            return (
              <Card key={b.broker} className={'card-surface border ' + accent} data-testid={`broker-card-${b.broker}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2.5">
                      <div className={'h-9 w-9 rounded-md flex items-center justify-center text-[10px] font-bold ' + badgeBg}>
                        {isPO ? 'PO' : isQX ? 'QX' : '—'}
                      </div>
                      <div>
                        <div className="font-semibold text-sm">{b.label}</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Broker totals · {data?.range?.days || '?'}d</div>
                      </div>
                    </div>
                    <span className={'text-[10px] uppercase tracking-[0.18em] font-mono ' + text}>
                      ${(b.deposit_sum_usd || 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="rounded-md border border-white/5 bg-black/30 p-2 text-center">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Clicks</div>
                      <div className="font-display text-lg mt-0.5">{(b.clicks || 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-md border border-white/5 bg-black/30 p-2 text-center">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Regs</div>
                      <div className="font-display text-lg mt-0.5">{(b.registrations || 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-md border border-white/5 bg-black/30 p-2 text-center">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">FTDs</div>
                      <div className={'font-display text-lg mt-0.5 ' + text}>{(b.ftds || 0).toLocaleString()}</div>
                    </div>
                    <div className="rounded-md border border-white/5 bg-black/30 p-2 text-center">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">Redep</div>
                      <div className="font-display text-lg mt-0.5">{(b.redeposits || 0).toLocaleString()}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filter Bar */}
      <Card className="card-surface" data-testid="broker-analytics-filter">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <CalendarRange className="h-4 w-4 text-[hsl(43_92%_56%)]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mr-2">Range</span>
            {QUICK_RANGES.map((r) => {
              const active = !useCustom && quick === r.key;
              return (
                <Button
                  key={r.key}
                  size="sm"
                  variant={active ? 'default' : 'outline'}
                  onClick={() => { setUseCustom(false); setQuick(r.key); }}
                  className={active ? 'h-8 cta-sheen text-white border-0' : 'h-8 border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white'}
                  data-testid={`broker-range-${r.key}`}
                >
                  {r.label}
                </Button>
              );
            })}
            <Button
              size="sm"
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              className="h-8 ml-auto border-white/10 bg-white/[0.02] text-muted-foreground hover:text-white"
              data-testid="broker-range-refresh"
            >
              <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} className="h-4 w-4 accent-[hsl(43_92%_56%)]" />
              <span className="text-xs text-muted-foreground">Custom</span>
            </label>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} disabled={!useCustom} max={end} className="h-9 w-[150px] bg-white/[0.02] border-white/10" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} disabled={!useCustom} min={start} max={todayIso()} className="h-9 w-[150px] bg-white/[0.02] border-white/10" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((k) => (
          <Card key={k.label} className="card-surface" data-testid={k.test}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{k.label}</span>
                <k.Icon className="h-4 w-4 text-[hsl(43_92%_56%)]" />
              </div>
              <div className="font-display uppercase text-3xl mt-2 text-[hsl(43_92%_56%)]">{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Conversion rates */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {rates.map((r) => (
          <div key={r.label} className="rounded-md border border-border bg-white/[0.02] p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{r.label}</div>
            <div className="mt-1 text-lg font-semibold text-white">{r.value}</div>
          </div>
        ))}
      </div>

      {/* Platform chart */}
      <Card className="card-surface" data-testid="broker-platform-chart">
        <CardHeader>
          <CardTitle className="text-base">Conversions by Platform · Clicks → Registrations → FTDs</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {platformChart.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No broker traffic in this range yet. Click the Pocket Option button on the live site to record events,
              and configure the postback URLs for live registration/deposit tracking.
            </div>
          ) : (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformChart}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="3 3" />
                    <XAxis dataKey="platform" stroke="#888" fontSize={11} />
                    <YAxis stroke="#888" fontSize={11} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: '#17171B', border: '1px solid #2B2B33' }} />
                    <Legend />
                    <Bar dataKey="clicks" name="Clicks" fill="#F5B942" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="registrations" name="Registrations" fill="#22C784" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="ftds" name="FTDs" fill="#E11D2E" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Platform</TableHead>
                    <TableHead className="text-right">Clicks</TableHead>
                    <TableHead className="text-right">Regs</TableHead>
                    <TableHead className="text-right">FTDs</TableHead>
                    <TableHead className="text-right">Deposits ($)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {platformChart.map((r) => (
                    <TableRow key={r.platform} data-testid={`broker-platform-${r.platform}`}>
                      <TableCell className="font-medium capitalize">{r.platform}</TableCell>
                      <TableCell className="text-right">{r.clicks}</TableCell>
                      <TableCell className="text-right">{r.registrations}</TableCell>
                      <TableCell className="text-right">{r.ftds}</TableCell>
                      <TableCell className="text-right text-[hsl(43_92%_56%)]">${(r.deposit_sum_usd || 0).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      {/* Country breakdown */}
      <Card className="card-surface">
        <CardHeader><CardTitle className="text-base">By Country · Top 20</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Clicks</TableHead>
                <TableHead className="text-right">Regs</TableHead>
                <TableHead className="text-right">FTDs</TableHead>
                <TableHead className="text-right">Deposits ($)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.by_country || [])
                .filter((r) => !isPlaceholder(r.country_code) && r.country_code !== '??')
                .map((r) => (
                  <TableRow key={r.country_code}>
                    <TableCell className="font-medium">{FLAGS[r.country_code] || '🌐'} {r.country_name || r.country_code}</TableCell>
                    <TableCell className="text-right">{r.clicks}</TableCell>
                    <TableCell className="text-right">{r.registrations}</TableCell>
                    <TableCell className="text-right">{r.ftds}</TableCell>
                    <TableCell className="text-right text-[hsl(43_92%_56%)]">${(r.deposit_sum_usd || 0).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              {(data?.by_country || []).filter((r) => !isPlaceholder(r.country_code) && r.country_code !== '??').length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-muted-foreground text-sm text-center py-6">No data yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent conversions */}
      <Card className="card-surface">
        <CardHeader><CardTitle className="text-base">Recent Conversions (last 20)</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Broker</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Trader ID</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.recent || []).map((r, i) => {
                const cc = clean(r.country_code, '');
                const trader = clean(r.trader_id, '—');
                const rowBroker = (r.broker || 'po').toLowerCase();
                const isPO = rowBroker === 'po';
                const brokerBadge = isPO
                  ? 'bg-[rgba(245,185,66,0.14)] text-[hsl(43_92%_56%)] border-[rgba(245,185,66,0.42)]'
                  : 'bg-[rgba(108,176,255,0.14)] text-[hsl(212_80%_70%)] border-[rgba(108,176,255,0.42)]';
                // Withdrawal events store the amount in `sumwithdraw` (not `sumdep`).
                // Show withdrawals in red with a leading minus, deposits in gold.
                const evLower = (r.event || '').toLowerCase();
                const isWithdrawal = evLower === 'withdrawal';
                const amountVal = isWithdrawal
                  ? Number(r.sumwithdraw || 0)
                  : Number(r.sumdep || 0);
                const amountColor = isWithdrawal
                  ? 'text-[hsl(0_72%_64%)]'
                  : 'text-[hsl(43_92%_56%)]';
                const amountPrefix = isWithdrawal && amountVal > 0 ? '-$' : '$';
                return (
                  <TableRow key={i} data-testid={`broker-recent-row-${i}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{(r.created_at || '').slice(0, 19).replace('T', ' ')}</TableCell>
                    <TableCell>
                      <span className={'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold border uppercase tracking-[0.14em] ' + brokerBadge}>
                        {isPO ? 'PO' : 'QX'}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium uppercase text-xs tracking-wider">{r.event}</TableCell>
                    <TableCell>
                      {cc ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span>{FLAGS[cc] || '🌐'}</span>
                          <span className="text-xs">{cc}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{trader}</TableCell>
                    <TableCell className={'text-right ' + amountColor} data-testid={`broker-recent-amount-${i}`}>
                      {amountPrefix}{amountVal.toLocaleString()}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(data?.recent || []).length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-muted-foreground text-sm text-center py-6">No conversions yet for this filter.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
